import { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useToast } from '../components/ui/use-toast';
import { Edit2, Check, X, Save } from 'lucide-react';

interface RetailMediaPricing {
  id: string;
  itemId: string;
  setupPriceCents: number;
  recurringPriceCents: number;
}

interface VenueTypeMultiplier {
  id: string;
  itemId: string;
  venueType: string;
  setupMultiplier: number;
  recurringMultiplier: number;
}

// Retail media items list
const RETAIL_MEDIA_ITEMS = [
  { id: 'dooh-ad', name: 'DOOH Ad | 1 Slot/60s' },
  { id: 'home-banner', name: 'Home Banner Ad' },
  { id: 'category-icon-lead', name: 'Category Icon Lead' },
  { id: 'category-banner', name: 'Category Banner Ad' },
  { id: 'premium-listing', name: 'Premium Product Listing' },
  { id: 'exclusive-category', name: 'Exclusive Category' },
  { id: 'enhanced-detail', name: 'Enhanced Product Detail' },
  { id: 'vend-video', name: 'Vend Video Ad' },
  { id: 'virtual-sku', name: 'Virtual SKU Listing' },
  { id: 'bundle-sku', name: 'Bundle SKU Listing' },
  { id: 'subscription-sku', name: 'Subscription SKU Listing' },
  { id: 'safe-qr', name: 'SafeQR Redirect' },
  { id: 'customer-survey', name: 'Customer Survey' },
  { id: 'customer-signup', name: 'Customer Sign-Up' },
];

export default function AdminRetailMediaPricing() {
  const [basePrices, setBasePrices] = useState<RetailMediaPricing[]>([]);
  const [multipliers, setMultipliers] = useState<VenueTypeMultiplier[]>([]);
  const [venueTypes, setVenueTypes] = useState<string[]>([]);
  const [selectedVenueType, setSelectedVenueType] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [editingPrice, setEditingPrice] = useState<string | null>(null);
  const [editingMultiplier, setEditingMultiplier] = useState<string | null>(null);
  const [editPriceSetup, setEditPriceSetup] = useState('');
  const [editPriceRecurring, setEditPriceRecurring] = useState('');
  const [editMultiplierSetup, setEditMultiplierSetup] = useState('');
  const [editMultiplierRecurring, setEditMultiplierRecurring] = useState('');
  const { toast } = useToast();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        loadBasePrices(),
        loadMultipliers(),
        loadVenueTypes(),
      ]);
    } catch (error) {
      console.error('Error loading data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load pricing data',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const loadBasePrices = async () => {
    const res = await fetch('/api/admin/retail-media-pricing', { credentials: 'include' });
    if (res.ok) {
      const data = await res.json();
      setBasePrices(data);
    }
  };

  const loadMultipliers = async () => {
    const res = await fetch('/api/admin/retail-media-pricing/multipliers', { credentials: 'include' });
    if (res.ok) {
      const data = await res.json();
      setMultipliers(data);
    }
  };

  const loadVenueTypes = async () => {
    try {
      // Fetch venue types from machines
      const machinesRes = await fetch('/api/store/venue-types', { credentials: 'include' });
      const machineVenueTypes: string[] = [];
      if (machinesRes.ok) {
        const machinesData = await machinesRes.json();
        machineVenueTypes.push(...machinesData.map((vt: any) => vt.venueType));
      }

      // Also fetch venue types from FieldOptions (so we can show venue types even without machines)
      const fieldOptionsRes = await fetch('/api/admin/field-options?fieldName=venueType', { credentials: 'include' });
      const fieldOptionVenueTypes: string[] = [];
      if (fieldOptionsRes.ok) {
        const fieldOptionsData = await fieldOptionsRes.json();
        fieldOptionVenueTypes.push(...fieldOptionsData.map((opt: any) => opt.value).filter(Boolean));
      }

      // Combine both sources and remove duplicates
      const combinedVenueTypes = [...machineVenueTypes, ...fieldOptionVenueTypes];
      const uniqueVenueTypes = Array.from(new Set(combinedVenueTypes)).sort();
      setVenueTypes(uniqueVenueTypes);
      
      // Set default selected venue type to first one if available
      if (uniqueVenueTypes.length > 0 && !selectedVenueType) {
        setSelectedVenueType(uniqueVenueTypes[0]);
      }
    } catch (error) {
      console.error('Error loading venue types:', error);
      // Fallback to just machines if FieldOptions fails
      const res = await fetch('/api/store/venue-types', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setVenueTypes(data.map((vt: any) => vt.venueType));
      }
    }
  };

  const getBasePrice = (itemId: string): RetailMediaPricing | undefined => {
    return basePrices.find(p => p.itemId === itemId);
  };

  const getMultiplier = (itemId: string, venueType: string): VenueTypeMultiplier | undefined => {
    return multipliers.find(m => m.itemId === itemId && m.venueType === venueType);
  };

  const handleSaveBasePrice = async (itemId: string) => {
    const setupCents = Math.round(parseFloat(editPriceSetup) * 100);
    const recurringCents = Math.round(parseFloat(editPriceRecurring) * 100);

    if (isNaN(setupCents) || isNaN(recurringCents) || setupCents < 0 || recurringCents < 0) {
      toast({
        title: 'Error',
        description: 'Please enter valid prices',
        variant: 'destructive',
      });
      return;
    }

    try {
      const res = await fetch('/api/admin/retail-media-pricing', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          itemId,
          setupPriceCents: setupCents,
          recurringPriceCents: recurringCents,
        }),
      });

      if (res.ok) {
        await loadBasePrices();
        setEditingPrice(null);
        toast({
          title: 'Success',
          description: 'Base price updated successfully',
        });
      } else {
        const error = await res.json();
        throw new Error(error.error || 'Failed to save price');
      }
    } catch (error) {
      console.error('Error saving base price:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to save base price',
        variant: 'destructive',
      });
    }
  };

  const handleSaveMultiplier = async (itemId: string, venueType: string) => {
    const setupMult = parseFloat(editMultiplierSetup);
    const recurringMult = parseFloat(editMultiplierRecurring);

    if (isNaN(setupMult) || isNaN(recurringMult) || setupMult < 0 || recurringMult < 0) {
      toast({
        title: 'Error',
        description: 'Please enter valid multipliers',
        variant: 'destructive',
      });
      return;
    }

    try {
      const res = await fetch('/api/admin/retail-media-pricing/multipliers', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          itemId,
          venueType,
          setupMultiplier: setupMult,
          recurringMultiplier: recurringMult,
        }),
      });

      if (res.ok) {
        await loadMultipliers();
        setEditingMultiplier(null);
        toast({
          title: 'Success',
          description: 'Multiplier updated successfully',
        });
      } else {
        const error = await res.json();
        throw new Error(error.error || 'Failed to save multiplier');
      }
    } catch (error) {
      console.error('Error saving multiplier:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to save multiplier',
        variant: 'destructive',
      });
    }
  };

  const startEditingPrice = (itemId: string) => {
    const price = getBasePrice(itemId);
    setEditingPrice(itemId);
    setEditPriceSetup(price ? (price.setupPriceCents / 100).toFixed(2) : '0.00');
    setEditPriceRecurring(price ? (price.recurringPriceCents / 100).toFixed(2) : '0.00');
  };

  const startEditingMultiplier = (itemId: string, venueType: string) => {
    const multiplier = getMultiplier(itemId, venueType);
    setEditingMultiplier(`${itemId}-${venueType}`);
    setEditMultiplierSetup(multiplier ? multiplier.setupMultiplier.toFixed(2) : '1.00');
    setEditMultiplierRecurring(multiplier ? multiplier.recurringMultiplier.toFixed(2) : '1.00');
  };

  const cancelEditing = () => {
    setEditingPrice(null);
    setEditingMultiplier(null);
  };

  if (loading) {
    return (
      <div className="w-full px-2 md:px-4 py-4 md:py-8">
        <div className="text-center">Loading...</div>
      </div>
    );
  }

  return (
    <div className="w-full px-2 md:px-4 py-4 md:py-8">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Retail Media Pricing</h1>
        <p className="text-gray-600 mt-2">
          Set base prices for retail media items and apply venue-type multipliers. Multipliers allow different pricing per venue type (e.g., Health & Fitness: 0.8x).
        </p>
      </div>

      {/* Base Prices Section */}
      <Card className="mb-8">
        <CardHeader>
          <CardTitle>Base Prices</CardTitle>
          <CardDescription>
            Set the standard setup and recurring prices for each retail media item. These are the base prices before multipliers are applied.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {RETAIL_MEDIA_ITEMS.map((item) => {
              const price = getBasePrice(item.id);
              const isEditing = editingPrice === item.id;

              return (
                <div
                  key={item.id}
                  className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50"
                >
                  <div className="flex-1">
                    <div className="font-medium text-gray-900">{item.name}</div>
                    <div className="text-sm text-gray-500">{item.id}</div>
                  </div>

                  {isEditing ? (
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <Label htmlFor={`setup-${item.id}`} className="text-sm w-20">Setup:</Label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">£</span>
                          <Input
                            id={`setup-${item.id}`}
                            type="number"
                            min="0"
                            step="0.01"
                            value={editPriceSetup}
                            onChange={(e) => setEditPriceSetup(e.target.value)}
                            className="pl-8 w-32"
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                handleSaveBasePrice(item.id);
                              } else if (e.key === 'Escape') {
                                cancelEditing();
                              }
                            }}
                            autoFocus
                          />
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Label htmlFor={`recurring-${item.id}`} className="text-sm w-24">4 Weeks:</Label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">£</span>
                          <Input
                            id={`recurring-${item.id}`}
                            type="number"
                            min="0"
                            step="0.01"
                            value={editPriceRecurring}
                            onChange={(e) => setEditPriceRecurring(e.target.value)}
                            className="pl-8 w-32"
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                handleSaveBasePrice(item.id);
                              } else if (e.key === 'Escape') {
                                cancelEditing();
                              }
                            }}
                          />
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="default"
                          onClick={() => handleSaveBasePrice(item.id)}
                        >
                          <Check className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={cancelEditing}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-6">
                      <div>
                        <span className="text-sm text-gray-500">Setup: </span>
                        <span className="text-lg font-semibold text-gray-900">
                          £{price ? (price.setupPriceCents / 100).toFixed(2) : '0.00'}
                        </span>
                      </div>
                      <div>
                        <span className="text-sm text-gray-500">4 Weeks: </span>
                        <span className="text-lg font-semibold text-gray-900">
                          £{price ? (price.recurringPriceCents / 100).toFixed(2) : '0.00'}
                        </span>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => startEditingPrice(item.id)}
                      >
                        <Edit2 className="h-4 w-4 mr-2" />
                        Edit
                      </Button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Multipliers Section */}
      <Card>
        <CardHeader>
          <CardTitle>Venue Type Multipliers</CardTitle>
          <CardDescription>
            Set multipliers per item per venue type. For example, if Health & Fitness has a 0.8x multiplier for DOOH Ad, the final price will be base price × 0.8. Default is 1.0x (no change).
          </CardDescription>
        </CardHeader>
        <CardContent>
          {venueTypes.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No venue types available. Please add venue types in Settings first.
            </div>
          ) : (
            <>
              {/* Venue Type Selector */}
              <div className="mb-6">
                <Label htmlFor="venue-type-select" className="mb-2 block text-sm font-medium">
                  Select Venue Type
                </Label>
                <Select value={selectedVenueType} onValueChange={setSelectedVenueType}>
                  <SelectTrigger id="venue-type-select" className="w-full max-w-md">
                    <SelectValue placeholder="Select a venue type..." />
                  </SelectTrigger>
                  <SelectContent>
                    {venueTypes.map((vt) => (
                      <SelectItem key={vt} value={vt}>
                        {vt}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Multipliers List for Selected Venue Type */}
              {selectedVenueType && (
                <div className="space-y-4">
                  {RETAIL_MEDIA_ITEMS.map((item) => {
                    const multiplier = getMultiplier(item.id, selectedVenueType);
                    const multiplierKey = `${item.id}-${selectedVenueType}`;
                    const isEditing = editingMultiplier === multiplierKey;

                    return (
                      <div
                        key={item.id}
                        className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50"
                      >
                        <div className="flex-1">
                          <div className="font-medium text-gray-900">{item.name}</div>
                          <div className="text-sm text-gray-500">{item.id}</div>
                        </div>

                        {isEditing ? (
                          <div className="flex items-center gap-4">
                            <div className="flex items-center gap-2">
                              <Label htmlFor={`setup-${item.id}`} className="text-sm w-20">Setup:</Label>
                              <Input
                                id={`setup-${item.id}`}
                                type="number"
                                min="0"
                                step="0.01"
                                value={editMultiplierSetup}
                                onChange={(e) => setEditMultiplierSetup(e.target.value)}
                                className="w-24"
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    handleSaveMultiplier(item.id, selectedVenueType);
                                  } else if (e.key === 'Escape') {
                                    cancelEditing();
                                  }
                                }}
                                autoFocus
                              />
                              <span className="text-sm text-gray-500">x</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Label htmlFor={`recurring-${item.id}`} className="text-sm w-24">Recurring:</Label>
                              <Input
                                id={`recurring-${item.id}`}
                                type="number"
                                min="0"
                                step="0.01"
                                value={editMultiplierRecurring}
                                onChange={(e) => setEditMultiplierRecurring(e.target.value)}
                                className="w-24"
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    handleSaveMultiplier(item.id, selectedVenueType);
                                  } else if (e.key === 'Escape') {
                                    cancelEditing();
                                  }
                                }}
                              />
                              <span className="text-sm text-gray-500">x</span>
                            </div>
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant="default"
                                onClick={() => handleSaveMultiplier(item.id, selectedVenueType)}
                              >
                                <Check className="h-4 w-4 mr-1" />
                                Save
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={cancelEditing}
                              >
                                <X className="h-4 w-4 mr-1" />
                                Cancel
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center gap-6">
                            <div className="text-right">
                              <div className="text-sm text-gray-500">Setup Multiplier</div>
                              <div className="text-lg font-semibold text-gray-900">
                                {multiplier ? multiplier.setupMultiplier.toFixed(2) : '1.00'}x
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="text-sm text-gray-500">Recurring Multiplier</div>
                              <div className="text-lg font-semibold text-gray-900">
                                {multiplier ? multiplier.recurringMultiplier.toFixed(2) : '1.00'}x
                              </div>
                            </div>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => startEditingMultiplier(item.id, selectedVenueType)}
                            >
                              <Edit2 className="h-4 w-4 mr-2" />
                              Edit
                            </Button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

